import { useEffect } from "react";
import { Button } from "@electro-planner/ui";
import type { ProjectMeta } from "@electro-planner/domain";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meta: ProjectMeta;
  onSetField: (key: keyof ProjectMeta, value: string) => void;
};

type FieldSpec = {
  key: keyof ProjectMeta;
  label: string;
  placeholder: string;
  full?: boolean;
};

// Rad-ordning följer title blockets logik: uppdragsinfo överst, person + rev sist.
// "full" = kolumnspann 2 (t.ex. ritnings-namn är typiskt längre).
const FIELDS: ReadonlyArray<FieldSpec> = [
  { key: "companyName", label: "Företag", placeholder: "t.ex. Karlin El AB", full: true },
  { key: "projectName", label: "Uppdrag", placeholder: "t.ex. Villa Svedberg ombyggnad" },
  { key: "projectNumber", label: "Uppdragsnr", placeholder: "t.ex. 24-112" },
  { key: "clientName", label: "Beställare", placeholder: "t.ex. Familjen Svedberg", full: true },
  { key: "designer", label: "Konstruktör", placeholder: "Namn på den som ritar" },
  { key: "revision", label: "Revision", placeholder: "A" },
];

/**
 * Modal för ritningens metadata — samma fält som syns i title blocket.
 * Input skrivs tillbaka direkt i Yjs per onBlur så vi inte behöver "Spara"-knapp;
 * användarens ändringar persisteras även om modalen stängs abrupt.
 */
export function DocumentMetaDialog({ open, onOpenChange, meta, onSetField }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-[10vh]"
      onClick={() => onOpenChange(false)}
      role="presentation"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg border border-[var(--color-border)] bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Ritningsinställningar"
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-fg)]">
              Ritningsinställningar
            </h2>
            <p className="text-xs text-[var(--color-muted)]">
              Visas i title blocket när du exporterar PDF.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            aria-label="Stäng"
          >
            Stäng
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3 px-4 py-4">
          {FIELDS.map((f) => (
            <MetaField
              key={f.key}
              spec={f}
              value={meta[f.key] ?? ""}
              onCommit={(v) => onSetField(f.key, v)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MetaField({
  spec,
  value,
  onCommit,
}: {
  spec: FieldSpec;
  value: string;
  onCommit: (v: string) => void;
}) {
  return (
    <label
      className={`flex flex-col gap-1 ${spec.full ? "col-span-2" : "col-span-1"}`}
    >
      <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted)]">
        {spec.label}
      </span>
      <input
        type="text"
        defaultValue={value}
        // defaultValue + onBlur ger ocontrollerad input — tillåter Yjs-ändringar
        // från andra klienter utan att överskriva caret under aktiv skrivning.
        // Värdet synkar vid blur och när komponenten remounts (dialog toggles).
        key={value}
        onBlur={(e) => {
          const v = e.currentTarget.value;
          if (v !== value) onCommit(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === "Escape") {
            (e.target as HTMLInputElement).value = value;
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={spec.placeholder}
        className="rounded border bg-white px-2 py-1 text-sm outline-none focus:ring-2"
        style={{ borderColor: "var(--color-border)" }}
      />
    </label>
  );
}
